// TalentHub.jsx - COMPLETE FIXED VERSION WITH DEBUG BUTTON
import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Star, 
  MapPin, 
  Briefcase, 
  Truck,
  User,
  Mail,
  Phone,
  Award,
  X,
  Calendar,
  Check,
  AlertTriangle,
  Building,
  Loader2,
  Shield,
  Eye,
  Bug
} from 'lucide-react';
import { driversAPI } from '../services/api'; // Import the API service
import axios from 'axios'; // Add axios for direct calls if needed

const TalentHub = () => {
  const [drivers, setDrivers] = useState([]);
  const [filteredDrivers, setFilteredDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [showDriverModal, setShowDriverModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [hiringDriverId, setHiringDriverId] = useState(null); // Track which driver is being hired
  const [activeTab, setActiveTab] = useState('overview');
  const [currentUser, setCurrentUser] = useState(null);
  const [hiringResult, setHiringResult] = useState({ success: false, message: '', driverName: '' });
  const [error, setError] = useState('');

  // ✅ FIXED: Function to fetch unassigned drivers
  const fetchUnassignedDrivers = async () => {
    try {
      setLoading(true);
      setError('');
      console.log('🔍 Fetching unassigned drivers from Talent Hub...');
      
      // Direct API call
      const token = localStorage.getItem('token');
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://fleet-county-backend.onrender.com/api';
      
      const response = await axios.get(`${API_BASE_URL}/drivers/unassigned`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('✅ Drivers fetched:', response.data);
      
      if (response.data?.success) {
        const driversData = response.data.data || [];
        console.log(`✅ Found ${driversData.length} drivers`);
        
        // Format drivers data
        const formattedDrivers = driversData.map(driver => ({
          id: driver.id || driver._id,
          driverId: driver.driverId || `DRV${(driver.id || driver._id).toString().slice(-6)}`,
          name: driver.name || 'Driver',
          email: driver.email || '',
          phone: driver.phone || '',
          location: driver.location || '',
          licenseNumber: driver.licenseNumber || '',
          driverType: driver.driverType || 'truck',
          experience: driver.experience || 0,
          certifications: driver.certifications || [],
          rating: driver.rating || 0,
          totalTrips: driver.totalTrips || 0,
          totalHours: driver.totalHours || 0,
          status: driver.status || 'available',
          availability: driver.availability || 'available',
          createdAt: driver.createdAt || new Date().toISOString(),
        }));
        
        setDrivers(formattedDrivers);
        setFilteredDrivers(formattedDrivers);
      } else {
        setError('Failed to fetch drivers: ' + (response.data?.message || 'Unknown error'));
      }
      
      setLoading(false);
    } catch (error) {
      console.error('❌ Error fetching drivers:', error);
      setError('Failed to load drivers. Please try again.');
      setLoading(false);
      setDrivers([]);
      setFilteredDrivers([]);
    }
  };

  // Fetch fresh user profile to ensure company data is up to date
  const fetchUserProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://fleet-county-backend.onrender.com/api';
      
      const response = await axios.get(`${API_BASE_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data?.success && response.data?.data) {
        const profile = response.data.data;
        console.log('📋 Updated user profile:', profile);
        localStorage.setItem('user', JSON.stringify(profile));
        setCurrentUser(profile);
        return profile;
      }
    } catch (error) {
      console.error('❌ Failed to fetch user profile:', error);
    }
    return null;
  };

  useEffect(() => {
    // Get current user from localStorage
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setCurrentUser(userData);
    
    console.log('👤 Current user from localStorage:', userData);
    
    // Fetch fresh user profile to ensure company data is up to date
    fetchUserProfile();
    fetchUnassignedDrivers();
  }, []);

  const handleViewDriver = (driver) => {
    setSelectedDriver(driver);
    setActiveTab('overview');
    setShowDriverModal(true);
  };

  // ✅ FIXED: Function to hire a driver (assign to company) - Only affects clicked button
  const hireDriver = async (driverId, driverName) => {
    if (assigning || hiringDriverId === driverId) return; // Prevent double click
    
    try {
      setAssigning(true);
      setHiringDriverId(driverId); // Track which driver is being hired
      setError('');
      
      console.log('🚀 Hiring driver:', { driverId, driverName });
      
      // Get fresh user data
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      console.log('👤 User data from localStorage:', userData);
      
      // IMPORTANT: Get company ID correctly
      let companyId = null;
      let companyName = null;
      
      // Try multiple ways to get company ID
      if (userData.companyId) {
        companyId = userData.companyId;
        console.log('✅ Got companyId from userData.companyId:', companyId);
      } 
      else if (userData.company) {
        console.log('🔍 Company object found:', userData.company);
        
        // Handle different formats
        if (typeof userData.company === 'object') {
          companyId = userData.company._id || userData.company.id;
          companyName = userData.company.name;
          console.log('✅ Got companyId from userData.company object:', companyId);
        } else if (typeof userData.company === 'string') {
          companyId = userData.company;
          console.log('✅ Got companyId from userData.company string:', companyId);
        }
      }
      // Try company from user profile
      else if (userData.profile?.company) {
        if (typeof userData.profile.company === 'object') {
          companyId = userData.profile.company._id || userData.profile.company.id;
          companyName = userData.profile.company.name;
        } else {
          companyId = userData.profile.company;
        }
        console.log('✅ Got companyId from userData.profile.company:', companyId);
      }
      
      console.log('🏢 Current companyId:', companyId);
      console.log('🏢 Company Name:', companyName);
      
      // If still no companyId, try to fetch fresh user data
      if (!companyId) {
        console.log('🔄 No companyId found, fetching fresh user profile...');
        const freshUserData = await fetchUserProfile();
        
        if (freshUserData?.company) {
          if (typeof freshUserData.company === 'object') {
            companyId = freshUserData.company._id || freshUserData.company.id;
            companyName = freshUserData.company.name;
          } else {
            companyId = freshUserData.company;
          }
          console.log('✅ Got companyId from fresh user data:', companyId);
        }
      }
      
      // Final check - if still no companyId, show error
      if (!companyId) {
        setHiringResult({
          success: false,
          message: 'You are not associated with any company. Please contact support.',
          driverName: driverName
        });
        setShowErrorModal(true);
        setAssigning(false);
        setHiringDriverId(null);
        return;
      }
      
      console.log('🎯 Final company details:', { companyId, companyName, userRole: userData.role });
      
      // Make the API call to hire driver
      const token = localStorage.getItem('token');
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://fleet-county-backend.onrender.com/api';
      
      console.log('📤 Sending hire request:', {
        driverId,
        companyId,
        endpoint: `${API_BASE_URL}/drivers/${driverId}/assign-company`,
        userRole: userData.role,
        userName: userData.name || userData.email
      });
      
      const response = await axios.post(
        `${API_BASE_URL}/drivers/${driverId}/assign-company`,
        { companyId },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 15000
        }
      );
      
      console.log('✅ Hire response:', response.data);
      
      if (response.data?.success) {
        // Remove the hired driver from the list
        const updatedDrivers = drivers.filter(driver => driver.id !== driverId);
        setDrivers(updatedDrivers);
        setFilteredDrivers(updatedDrivers);
        
        setHiringResult({
          success: true,
          message: response.data.message || 'Driver hired successfully!',
          driverName: driverName,
          companyName: response.data.data?.company?.name || companyName || 'Your company',
          driverData: response.data.data?.driver
        });
        
        // Close modals
        setShowDriverModal(false);
        setSelectedDriver(null);
        setShowSuccessModal(true);
        
      } else {
        setHiringResult({
          success: false,
          message: response.data?.message || 'Failed to hire driver',
          driverName: driverName
        });
        setShowErrorModal(true);
      }
      
    } catch (err) {
      console.error('❌ Hiring error:', err);
      
      let errorMessage = 'Failed to hire driver. Please try again.';
      let errorDetails = err.response?.data;
      
      console.log('Error details:', errorDetails);
      console.log('Error status:', err.response?.status);
      
      if (err.response?.status === 403) {
        if (errorDetails?.message) {
          errorMessage = errorDetails.message;
        } else {
          errorMessage = 'You are not authorized to hire drivers. Make sure you are a company admin and associated with a company.';
        }
      } else if (err.response?.status === 400) {
        if (errorDetails?.alreadyAssigned) {
          errorMessage = errorDetails.message || 'This driver is already hired by another company.';
          // Remove from list
          const updatedDrivers = drivers.filter(driver => driver.id !== driverId);
          setDrivers(updatedDrivers);
          setFilteredDrivers(updatedDrivers);
        } else if (errorDetails?.message) {
          errorMessage = errorDetails.message;
        }
      } else if (err.response?.status === 404) {
        errorMessage = 'Driver or company not found.';
      } else if (err.response?.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (err.message?.includes('timeout')) {
        errorMessage = 'Request timeout. Please try again.';
      } else if (err.message?.includes('Network Error')) {
        errorMessage = 'Network error. Please check your internet connection.';
      }
      
      setHiringResult({
        success: false,
        message: errorMessage,
        driverName: driverName
      });
      setShowErrorModal(true);
    } finally {
      setAssigning(false);
      setHiringDriverId(null); // Reset when done
    }
  };

  // Debug function to check user data
  const debugUserData = () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    console.log('🔍 DEBUG User Data:', user);
    
    // Extract company info
    let companyInfo = 'No company found';
    if (user.company) {
      if (typeof user.company === 'object') {
        companyInfo = `Company: ${user.company.name || 'No name'} (ID: ${user.company._id || user.company.id})`;
      } else {
        companyInfo = `Company ID: ${user.company}`;
      }
    } else if (user.companyId) {
      companyInfo = `Company ID: ${user.companyId}`;
    }
    
    alert(`
      👤 USER DEBUG INFO:
      
      Role: ${user.role || 'Unknown'}
      Name: ${user.name || user.email || 'Unknown'}
      Email: ${user.email || 'Unknown'}
      
      🏢 COMPANY INFO:
      ${companyInfo}
      
      🔑 TOKEN PRESENT: ${localStorage.getItem('token') ? 'Yes' : 'No'}
      
      Full user object logged to console.
    `);
  };

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    
    if (!term.trim()) {
      setFilteredDrivers(drivers);
      return;
    }
    
    const filtered = drivers.filter(driver => {
      const searchableText = `
        ${driver.name || ''} 
        ${driver.licenseNumber || ''} 
        ${driver.email || ''} 
        ${driver.driverId || ''} 
        ${driver.phone || ''} 
        ${driver.location || ''} 
        ${driver.driverType || ''}
      `.toLowerCase();
      
      return searchableText.includes(term);
    });
    
    setFilteredDrivers(filtered);
  };

  const formatPhoneForCall = (phone) => {
    return phone ? phone.replace(/[^\d+]/g, '') : '';
  };

  // Render driver rating stars
  const renderStars = (rating) => {
    return (
      <div className="flex items-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${
              star <= (rating || 0)
                ? 'text-yellow-500 fill-yellow-500'
                : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle size={20} />
              <span>{error}</span>
            </div>
            <button
              onClick={() => setError('')}
              className="text-red-700 hover:text-red-900"
            >
              <X size={20} />
            </button>
          </div>
        )}

        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:justify-between md:items-start mb-4 gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Talent Hub</h1>
              <p className="text-gray-600">Find and hire qualified drivers for your company</p>
              {/* <p className="text-sm text-gray-500 mt-1">
                Showing {filteredDrivers.length} available {filteredDrivers.length === 1 ? 'driver' : 'drivers'}
              </p> */}
            </div>
            
            {currentUser && (
              <div className="bg-white p-4 rounded-lg border shadow-sm min-w-[250px]">
                <div className="flex items-center">
                  <Building className="w-5 h-5 text-[#e28743] mr-2" />
                  <div>
                    <p className="font-medium text-gray-900 truncate">{currentUser.name || currentUser.email}</p>
                    <p className="text-sm text-gray-500">
                      {currentUser.role === 'company_admin' ? 'Company Admin' : 
                       currentUser.role === 'super_admin' ? 'Super Admin' : 
                       currentUser.role || 'User'}
                    </p>
                    {currentUser.company && (
                      <p className="text-xs text-gray-400 mt-1 truncate">
                        Company: {typeof currentUser.company === 'object' ? currentUser.company.name : 'Associated'}
                      </p>
                    )}
                    
                    {/* DEBUG BUTTON */}
                    <div className="flex gap-2 mt-2">
                      {/* <button
                        onClick={debugUserData}
                        className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                      >
                        <Bug size={12} />
                        Debug User
                      </button> */}
                      {/* <button
                        onClick={fetchUnassignedDrivers}
                        className="text-xs text-[#e28743] hover:text-[#d47834] flex items-center gap-1"
                      >
                        <Loader2 className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                      </button> */}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white p-4 rounded-lg border shadow-sm">
              <p className="text-2xl font-bold text-[#e28743]">{drivers.length}</p>
              <p className="text-gray-600">Available Drivers</p>
            </div>
            <div className="bg-white p-4 rounded-lg border shadow-sm">
              <p className="text-2xl font-bold text-green-600">
                {drivers.filter(d => d.rating >= 4).length}
              </p>
              <p className="text-gray-600">4+ Star Rated</p>
            </div>
            <div className="bg-white p-4 rounded-lg border shadow-sm">
              <p className="text-2xl font-bold text-blue-600">
                {drivers.filter(d => d.experience >= 5).length}
              </p>
              <p className="text-gray-600">5+ Years Exp</p>
            </div>
            <div className="bg-white p-4 rounded-lg border shadow-sm">
              <p className="text-2xl font-bold text-purple-600">
                {drivers.filter(d => d.certifications?.length > 0).length}
              </p>
              <p className="text-gray-600">Certified Drivers</p>
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative mb-8">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#e28743] focus:border-transparent"
              placeholder="Search drivers by name, license, location, or type..."
              value={searchTerm}
              onChange={handleSearch}
            />
            {searchTerm && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setFilteredDrivers(drivers);
                }}
                className="absolute inset-y-0 right-0 pr-4 flex items-center"
              >
                <X className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e28743] mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading available drivers...</p>
          </div>
        )}

        {/* Drivers Grid */}
        {!loading && filteredDrivers.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredDrivers.map((driver) => (
              <div key={driver.id} className="bg-white rounded-lg shadow border hover:shadow-md transition-shadow duration-300">
                <div className="p-6">
                  {/* Driver Header */}
                  <div className="flex items-center mb-4">
                    <div className="w-14 h-14 bg-gradient-to-r from-orange-100 to-amber-100 rounded-full flex items-center justify-center mr-4">
                      <User className="w-7 h-7 text-[#e28743]" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h3 className="font-bold text-gray-900 text-lg truncate">{driver.name}</h3>
                        {driver.certifications?.length > 0 && (
                          <Shield className="w-5 h-5 text-green-500 flex-shrink-0" title="Certified" />
                        )}
                      </div>
                      <p className="text-gray-500 text-sm">ID: {driver.driverId}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="inline-block px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Available
                        </span>
                        <span className="inline-block px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                          {driver.driverType}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Quick Info */}
                  <div className="space-y-3 mb-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Star className="w-4 h-4 text-yellow-500 mr-2" />
                        <span className="font-medium">{driver.rating?.toFixed(1) || 'N/A'}</span>
                        <span className="text-gray-500 text-sm ml-1">rating</span>
                      </div>
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 text-blue-500 mr-2" />
                        <span className="text-gray-600">{driver.experience} yrs</span>
                      </div>
                    </div>

                    {driver.location && (
                      <div className="flex items-center">
                        <MapPin className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                        <span className="text-gray-600 truncate">{driver.location}</span>
                      </div>
                    )}

                    {driver.licenseNumber && (
                      <div className="flex items-center">
                        <Briefcase className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                        <span className="text-gray-600">License: {driver.licenseNumber}</span>
                      </div>
                    )}
                  </div>

                  {/* Contact Preview */}
                  {(driver.phone || driver.email) && (
                    <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm font-medium text-gray-700 mb-2">Contact Info:</p>
                      <div className="flex flex-wrap gap-3">
                        {driver.phone && (
                          <a 
                            href={`tel:${formatPhoneForCall(driver.phone)}`}
                            className="flex items-center text-sm text-blue-600 hover:text-blue-800 transition-colors"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Phone className="w-3 h-3 mr-1" />
                            Call
                          </a>
                        )}
                        {driver.email && (
                          <a 
                            href={`mailto:${driver.email}`}
                            className="flex items-center text-sm text-blue-600 hover:text-blue-800 transition-colors"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Mail className="w-3 h-3 mr-1" />
                            Email
                          </a>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Stats */}
                  <div className="flex justify-between mb-6 text-sm border-t pt-4">
                    <div className="text-center">
                      <p className="font-bold text-gray-800">{driver.totalTrips || 0}</p>
                      <p className="text-gray-500">Trips</p>
                    </div>
                    <div className="text-center">
                      <p className="font-bold text-gray-800">{driver.totalHours || 0}</p>
                      <p className="text-gray-500">Hours</p>
                    </div>
                    <div className="text-center">
                      <p className="font-bold text-gray-800">{driver.certifications?.length || 0}</p>
                      <p className="text-gray-500">Certs</p>
                    </div>
                    <div className="text-center">
                      <p className="font-bold text-gray-800">{driver.experience || 0}</p>
                      <p className="text-gray-500">Yrs Exp</p>
                    </div>
                  </div>

                  {/* Action Buttons - Only clicked button shows loading */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleViewDriver(driver)}
                      disabled={assigning && hiringDriverId === driver.id}
                      className="flex-1 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                    >
                      <Eye size={16} />
                      View
                    </button>
                    <button
                      onClick={() => hireDriver(driver.id, driver.name)}
                      disabled={assigning && hiringDriverId === driver.id}
                      className="flex-1 py-2.5 bg-gradient-to-r from-[#e28743] to-orange-500 text-white rounded-lg hover:from-[#d47834] hover:to-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
                    >
                      {assigning && hiringDriverId === driver.id ? (
                        <>
                          <Loader2 size={16} className="animate-spin" />
                          Hiring...
                        </>
                      ) : (
                        <>
                          <Check size={16} />
                          Hire Now
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State - No Drivers */}
        {!loading && drivers.length === 0 && (
          <div className="text-center py-16 bg-white rounded-lg border shadow-sm">
            <User className="w-20 h-20 text-gray-300 mx-auto mb-4" />
            <h3 className="text-2xl font-semibold text-gray-700 mb-3">No Drivers Available</h3>
            <p className="text-gray-500 max-w-md mx-auto mb-6">
              All drivers have been hired. Check back later or try refreshing the page.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={fetchUnassignedDrivers}
                className="px-6 py-3 bg-[#e28743] text-white rounded-lg hover:bg-[#d47834] transition-colors flex items-center gap-2"
              >
                <Loader2 className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                onClick={debugUserData}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                <Bug size={16} />
                Debug
              </button>
            </div>
          </div>
        )}

        {/* Empty State - No Search Results */}
        {!loading && searchTerm && filteredDrivers.length === 0 && drivers.length > 0 && (
          <div className="text-center py-12 bg-white rounded-lg border">
            <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No matching drivers found</h3>
            <p className="text-gray-500 mb-6">No drivers match "{searchTerm}"</p>
            <button
              onClick={() => {
                setSearchTerm('');
                setFilteredDrivers(drivers);
              }}
              className="px-6 py-2.5 bg-[#e28743] text-white rounded-lg hover:bg-[#d47834] transition-colors"
            >
              Show All Drivers
            </button>
          </div>
        )}
      </div>

      {/* Driver Details Modal */}
      {showDriverModal && selectedDriver && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-[#e28743] to-[#f0a767] p-6 text-white">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-2xl font-bold mb-1">{selectedDriver.name}</h2>
                  <p className="opacity-90">{selectedDriver.driverId} • {selectedDriver.driverType} Driver</p>
                </div>
                <button 
                  onClick={() => setShowDriverModal(false)}
                  className="p-2 hover:bg-white/20 rounded-full transition-colors"
                  disabled={assigning && hiringDriverId === selectedDriver.id}
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              {/* Status Badges */}
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  Available for Hire
                </span>
                {selectedDriver.experience > 5 && (
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                    {selectedDriver.experience}+ Years Experience
                  </span>
                )}
                {selectedDriver.certifications?.length > 0 && (
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                    {selectedDriver.certifications.length} Certifications
                  </span>
                )}
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {/* Tabs */}
              <div className="border-b mb-6">
                <nav className="flex space-x-8">
                  {['overview', 'contact'].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`py-3 px-1 font-medium text-sm border-b-2 transition-colors ${
                        activeTab === tab
                          ? 'border-[#e28743] text-[#e28743]'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                      disabled={assigning && hiringDriverId === selectedDriver.id}
                    >
                      {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    </button>
                  ))}
                </nav>
              </div>

              {/* Tab Content */}
              <div className="space-y-6">
                {activeTab === 'overview' && (
                  <>
                    {/* Rating & Experience */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div className="bg-gradient-to-br from-yellow-50 to-amber-50 p-5 rounded-xl border border-yellow-100">
                        <div className="flex items-center mb-3">
                          <Star className="w-6 h-6 text-yellow-500 mr-3" />
                          <h3 className="font-bold text-lg text-gray-800">Driver Rating</h3>
                        </div>
                        <div className="flex items-center mb-3">
                          <span className="text-4xl font-bold mr-4">{selectedDriver.rating?.toFixed(1) || 'N/A'}</span>
                          <div className="flex">
                            {renderStars(selectedDriver.rating)}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div className="text-center">
                            <p className="font-bold text-gray-800 text-xl">{selectedDriver.totalTrips || 0}</p>
                            <p className="text-gray-600">Completed Trips</p>
                          </div>
                          <div className="text-center">
                            <p className="font-bold text-gray-800 text-xl">{selectedDriver.totalHours || 0}</p>
                            <p className="text-gray-600">Total Hours</p>
                          </div>
                        </div>
                      </div>

                      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-5 rounded-xl border border-blue-100">
                        <div className="flex items-center mb-3">
                          <Briefcase className="w-6 h-6 text-blue-500 mr-3" />
                          <h3 className="font-bold text-lg text-gray-800">Experience & Qualifications</h3>
                        </div>
                        <p className="text-4xl font-bold mb-2">{selectedDriver.experience || 0} years</p>
                        {selectedDriver.licenseNumber && (
                          <p className="text-gray-700 mb-4">
                            <span className="font-medium">License:</span> {selectedDriver.licenseNumber}
                          </p>
                        )}
                        {selectedDriver.certifications?.length > 0 && (
                          <div>
                            <p className="font-medium text-gray-700 mb-2">Certifications:</p>
                            <div className="flex flex-wrap gap-2">
                              {selectedDriver.certifications.map((cert, idx) => (
                                <span
                                  key={idx}
                                  className="px-3 py-1.5 bg-white border border-blue-200 text-blue-700 rounded-lg text-sm font-medium shadow-sm"
                                >
                                  {cert}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Location & Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="bg-white p-5 rounded-xl border shadow-sm">
                        <h3 className="font-bold text-lg mb-3 flex items-center">
                          <MapPin className="w-5 h-5 text-gray-500 mr-2" />
                          Location & Details
                        </h3>
                        {selectedDriver.location && (
                          <p className="text-gray-700 mb-3">{selectedDriver.location}</p>
                        )}
                        <div className="space-y-2">
                          <p className="text-gray-600">
                            <span className="font-medium">Driver Type:</span> <span className="capitalize">{selectedDriver.driverType}</span>
                          </p>
                          <p className="text-gray-600">
                            <span className="font-medium">Status:</span> <span className="capitalize">{selectedDriver.status || 'available'}</span>
                          </p>
                          <p className="text-gray-600">
                            <span className="font-medium">Available Since:</span> {new Date(selectedDriver.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>

                      <div className="bg-white p-5 rounded-xl border shadow-sm">
                        <h3 className="font-bold text-lg mb-3 flex items-center">
                          <Truck className="w-5 h-5 text-gray-500 mr-2" />
                          Driver Profile
                        </h3>
                        <p className="text-gray-700 mb-4">
                          {selectedDriver.experience || 0} years of professional driving experience with {selectedDriver.totalTrips || 0} completed trips.
                        </p>
                        <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded">
                          <div className="flex">
                            <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 mr-3 flex-shrink-0" />
                            <div>
                              <h4 className="font-bold text-amber-800">Recommendation</h4>
                              <p className="text-amber-700 text-sm mt-1">
                                This driver has been verified and is ready to join your team. Contact them to discuss specific requirements.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {activeTab === 'contact' && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Phone */}
                      {selectedDriver.phone && (
                        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-5 rounded-xl border border-blue-200">
                          <div className="flex items-center mb-4">
                            <Phone className="w-7 h-7 text-blue-500 mr-3" />
                            <h3 className="font-bold text-lg text-gray-800">Phone Contact</h3>
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-gray-900 mb-3">{selectedDriver.phone}</p>
                            <div className="flex gap-3">
                              <a
                                href={`tel:${formatPhoneForCall(selectedDriver.phone)}`}
                                className="flex-1 py-3 bg-blue-600 text-white rounded-lg text-center hover:bg-blue-700 transition-colors shadow-md"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Phone className="w-4 h-4 inline mr-2" />
                                Call Now
                              </a>
                              <a
                                href={`sms:${formatPhoneForCall(selectedDriver.phone)}`}
                                className="flex-1 py-3 border-2 border-blue-600 text-blue-600 rounded-lg text-center hover:bg-blue-50 transition-colors"
                                onClick={(e) => e.stopPropagation()}
                              >
                                Send SMS
                              </a>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Email */}
                      {selectedDriver.email && (
                        <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-5 rounded-xl border border-purple-200">
                          <div className="flex items-center mb-4">
                            <Mail className="w-7 h-7 text-purple-500 mr-3" />
                            <h3 className="font-bold text-lg text-gray-800">Email Contact</h3>
                          </div>
                          <div>
                            <p className="text-lg font-medium text-gray-900 mb-3 break-all">{selectedDriver.email}</p>
                            <a
                              href={`mailto:${selectedDriver.email}`}
                              className="inline-flex items-center justify-center w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-colors shadow-md"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Mail className="w-4 h-4 mr-2" />
                              Send Email
                            </a>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Hiring Recommendation */}
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500 p-5 rounded-lg">
                      <div className="flex">
                        <div className="mr-4">
                          <Check className="w-6 h-6 text-green-500 mt-0.5" />
                        </div>
                        <div>
                          <h4 className="font-bold text-green-800 text-lg">Ready to Hire?</h4>
                          <p className="text-green-700 mt-2">
                            This driver has been verified and is available immediately. We recommend:
                          </p>
                          <ul className="text-green-700 mt-2 space-y-1">
                            <li>• Contact the driver to confirm availability</li>
                            <li>• Discuss specific job requirements and expectations</li>
                            <li>• Schedule an interview or trial period if needed</li>
                            <li>• Complete the hiring process through the system</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="border-t p-6 bg-gray-50">
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                <div className="text-center sm:text-left">
                  <p className="text-gray-600">
                    <span className="font-medium">Ready to hire?</span> Contact the driver first to discuss details
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowDriverModal(false)}
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    disabled={assigning && hiringDriverId === selectedDriver.id}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => hireDriver(selectedDriver.id, selectedDriver.name)}
                    disabled={assigning && hiringDriverId === selectedDriver.id}
                    className="px-6 py-3 bg-gradient-to-r from-[#e28743] to-orange-500 text-white rounded-lg hover:from-[#d47834] hover:to-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center shadow-md hover:shadow-lg"
                  >
                    {assigning && hiringDriverId === selectedDriver.id ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Hire This Driver
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full transform transition-all duration-300 scale-100">
            <div className="p-8">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-6">
                  <Check className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  🎉 Driver Hired Successfully!
                </h3>
                
                <div className="mt-6">
                  <p className="text-lg text-gray-700 mb-6">
                    {hiringResult.message}
                  </p>
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-5 rounded-xl border border-green-200 mb-6">
                    <p className="font-bold text-green-800 text-xl mb-2">{hiringResult.driverName}</p>
                    {hiringResult.companyName && (
                      <p className="text-green-600">
                        Now part of <span className="font-semibold">{hiringResult.companyName}</span>
                      </p>
                    )}
                    <div className="mt-4 flex items-center justify-center">
                      <div className="h-1 w-12 bg-green-500 rounded-full mx-2"></div>
                      <Check className="w-5 h-5 text-green-500" />
                      <div className="h-1 w-12 bg-green-500 rounded-full mx-2"></div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-8">
                  <button
                    onClick={() => {
                      setShowSuccessModal(false);
                      setHiringResult({ success: false, message: '', driverName: '' });
                    }}
                    className="w-full px-6 py-3 bg-[#e28743] text-white rounded-lg hover:bg-[#d47834] transition-colors font-semibold shadow-md"
                  >
                    Continue Browsing Talent Hub
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-8">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-6">
                  <AlertTriangle className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  Unable to Hire Driver
                </h3>
                
                <div className="mt-6">
                  <p className="text-lg text-gray-700 mb-6">
                    {hiringResult.message}
                  </p>
                  {hiringResult.driverName && (
                    <div className="bg-red-50 p-5 rounded-xl border border-red-200 mb-6">
                      <p className="font-bold text-red-800">Driver: {hiringResult.driverName}</p>
                    </div>
                  )}
                </div>
                
                <div className="mt-8 space-y-3">
                  <button
                    onClick={() => {
                      setShowErrorModal(false);
                      setHiringResult({ success: false, message: '', driverName: '' });
                    }}
                    className="w-full px-6 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => {
                      setShowErrorModal(false);
                      fetchUnassignedDrivers();
                    }}
                    className="w-full px-6 py-3 bg-[#e28743] text-white rounded-lg hover:bg-[#d47834] transition-colors font-semibold"
                  >
                    Refresh Drivers
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TalentHub;